import React, { useState } from 'react';
import { ShieldCheck, ChevronDown, Download, Printer } from 'lucide-react';
import type { OwnerHomeData } from '../../../types/aai';
import { aaiApi } from '../../../services/aaiApi';
import { Card, MonoLabel, Banner, StatCard, Avatar, ApprovalQueue } from './ui';
import { exportOwnerCsv, downloadAdoptionReportPdf } from './dashboardExport';

const AdminHome: React.FC<{ data: OwnerHomeData; onRefresh: () => void }> = ({ data, onRefresh }) => {
  const s = data.stats;
  const [busy, setBusy] = useState<string | null>(null);
  const decide = async (id: string, decision: string, comment?: string) => {
    setBusy(id);
    try { await aaiApi.decideRequest(id, { decision, comment }); window.dispatchEvent(new Event('aai:approvals-changed')); onRefresh(); }
    finally { setBusy(null); }
  };
  return (
    <div className="space-y-4">
      <Banner tone="blue" icon={<ShieldCheck size={16} className="text-brand-500 shrink-0" />}>
        Workspace administration. Enrolling people reuses the existing Team invite — bulk-add becomes "invite all employees" for the AAI program.
      </Banner>

      <div className="flex items-center justify-between gap-3">
        <MonoLabel>Program health</MonoLabel>
        <div className="flex items-center gap-2">
          <button onClick={() => exportOwnerCsv(data)}
            className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-gray-600 hover:text-brand-700 border border-gray-200 rounded-lg px-3 py-1.5 whitespace-nowrap"><Download size={14} /> CSV</button>
          <button onClick={() => { void downloadAdoptionReportPdf(); }}
            className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-white bg-brand-500 hover:bg-brand-600 rounded-lg px-3 py-1.5 whitespace-nowrap"><Printer size={14} /> PDF</button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard value={<>{s.members}<span className="text-base font-medium text-gray-400"> / {s.seats}</span></>} label="Members · seats" />
        <StatCard value={s.maturity_suppressed ? '—' : `${s.maturity_pct ?? '—'}%`} label="Company maturity" />
        <StatCard value={s.onboarded} label="Onboarded" />
        <StatCard value={s.plan} label="Current plan" />
      </div>

      {/* gate 2 — final sign-off on requests a dept head forwarded (plus deptless requests) */}
      <ApprovalQueue items={data.approvals} onDecide={decide} busyId={busy} title="Requests awaiting your approval" />

      <div className="bg-white border border-gray-100 rounded-xl overflow-hidden">
        <div className="px-5 py-3.5 border-b border-gray-100 flex items-center justify-between">
          <span className="font-bold text-sm">Team & roles</span>
          <span className="text-xs text-gray-400">Workspace role drives each person's UI</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead><tr className="text-left bg-gray-50/70 text-gray-400">
              {['Member', 'Department', 'Workspace role', '→ System'].map((h) => (
                <th key={h} className="px-4 py-2.5 font-sans text-[10.5px] uppercase tracking-wider font-semibold whitespace-nowrap">{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {data.team.map((m) => (
                <tr key={m.email} className="border-t border-gray-50">
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2.5"><Avatar initials={m.initials} />
                      <div className="min-w-0"><div className="font-semibold text-[13px] truncate">{m.name}</div><div className="text-[11px] text-gray-400 truncate">{m.email}</div></div>
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-gray-600">{m.department}</td>
                  <td className="px-4 py-2.5">
                    <span className="inline-flex items-center gap-1.5 font-sans text-[11px] font-semibold px-2.5 py-1 rounded-lg bg-brand-50 text-brand-700">{m.role}<ChevronDown size={12} /></span>
                  </td>
                  <td className="px-4 py-2.5 font-sans text-[11px] text-brand-600 font-semibold">{m.system}</td>
                </tr>
              ))}
              {data.team.length === 0 && <tr><td colSpan={4} className="px-4 py-6 text-center text-[13px] text-gray-400">No members enrolled yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <MonoLabel className="mb-2">Billing</MonoLabel>
          <div className="text-sm font-semibold mb-0.5">{data.billing?.plan || s.plan} plan · {data.billing?.seats ?? s.seats} seats</div>
          <p className="text-[12.5px] text-gray-500">Owner-only — Admins and Champions don't see this card.</p>
        </Card>
        <Card>
          <MonoLabel className="mb-2">AAI program</MonoLabel>
          <div className="flex items-center justify-between">
            <div className="text-sm font-semibold">Center of Excellence</div>
            <div className="w-9 h-5 rounded-full bg-brand-500 relative"><div className="w-4 h-4 rounded-full bg-white absolute top-0.5 right-0.5" /></div>
          </div>
          <p className="text-[12.5px] text-gray-500 mt-1.5">Enabled · workspace role drives each person's lens.</p>
        </Card>
      </div>
    </div>
  );
};

export default AdminHome;
